/**
 * QPR LISTING COMPONENT
 */

import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { Table } from '../../shared/table/table';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ReportService } from '../shared/services/report.service';
import { QprDocument } from '../shared/models/report.model';

type QprStatusKey = 'approved' | 'rejected' | 'pending';

interface QprDocumentRow extends QprDocument {
  statusKey: QprStatusKey;
}

const STATUS_BADGE_MAP: Record<QprStatusKey, string> = {
  approved: 'status-approved',
  rejected: 'status-rejected',
  pending: 'status-pending',
};

@Component({
  selector: 'app-qpr-listing',
  standalone: true,
  imports: [CommonModule, Table, Popup, TranslatePipe],
  template: `
    <div class="p-6 space-y-6">
      <div>
        <h1 class="text-lg sm:text-xl font-semibold text-[#1E2A78]">
          {{ 'QPR_LISTING.TITLE' | translate }}
        </h1>
      </div>

      <div *ngIf="loadingDocuments" class="text-sm text-gray-500">
        {{ 'QPR_LISTING.LOADING' | translate }}
      </div>

      <div *ngIf="!loadingDocuments && hasDocumentsError" class="text-sm text-red-600">
        {{ 'QPR_LISTING.LOAD_FAILED' | translate }}
      </div>

      <app-table
        *ngIf="!loadingDocuments && !hasDocumentsError"
        [columns]="columns"
        [data]="documentRows"
        [actions]="tableActions"
      ></app-table>
    </div>

    <!-- Approve/Reject confirmation — reuses the shared app-popup's built-in "confirm" type. -->
    <app-popup
      [show]="confirmDialogVisible"
      (showChange)="confirmDialogVisible = $event"
      type="confirm"
      title="QPR_LISTING.CONFIRM_TITLE"
      [message]="confirmMessage"
      [confirmText]="'QPR_LISTING.CONFIRM' | translate"
      [cancelText]="'QPR_LISTING.CANCEL' | translate"
      (confirmed)="onConfirmAction()"
    ></app-popup>
  `,
})
export class QprListingComponent implements OnInit {
  private reportService = inject(ReportService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);

  private allDocuments: QprDocument[] = [];
  loadingDocuments = true;
  hasDocumentsError = false;
  submittingRows = new Set<number>();

  columns: TableColumn[] = [];

  // Approve/Reject confirmation dialog state.
  confirmDialogVisible = false;
  confirmMessage = '';
  private pendingDoc: QprDocument | null = null;
  private pendingAprvdFlg: number | null = null;

  get currentRole(): string {
    return (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
      .trim()
      .toUpperCase();
  }

  /** Approve/Reject stay visible only while the document hasn't been decided yet. */
  isPending(doc: QprDocument): boolean {
    return doc.aprvdFlg !== 1 && doc.aprvdFlg !== 2;
  }

  get tableActions(): TableAction[] {
    if (this.currentRole === 'LNLY_AGNT') {
      return [];
    }

    // `.action-btn` (global, shared by every app-table usage) only sets a
    // fixed box size — it doesn't center content or round corners, so every
    // action here supplies its own flex/rounded classes for a uniform,
    // centered look instead of relying on that shared class alone.
    const baseAction = 'flex h-8 w-8 items-center justify-center rounded-full transition-colors';

    return [
      {
        icon: faDownload,
        tooltip: this.translate.instant('QPR_LISTING.DOWNLOAD'),
        class: `${baseAction} text-gray-500 hover:bg-gray-100 hover:text-indigo-600`,
        iconSize: 'h-4 w-4',
        onClick: (row: QprDocumentRow) => this.downloadReport(row),
      },
      {
        icon: 'images/Tick-2.png',
        tooltip: this.translate.instant('QPR_LISTING.APPROVE'),
        class: `${baseAction} bg-green-100 hover:bg-green-200`,
        iconSize: 'h-4 w-4',
        visible: (row: QprDocumentRow) => this.isPending(row),
        hide: (row: QprDocumentRow) => this.submittingRows.has(row.qprDocSeq),
        onClick: (row: QprDocumentRow) => this.approveDocument(row),
      },
      {
        icon: 'images/cancel.png',
        tooltip: this.translate.instant('QPR_LISTING.REJECT'),
        class: `${baseAction} bg-red-100 hover:bg-red-200`,
        iconSize: 'h-4 w-4',
        visible: (row: QprDocumentRow) => this.isPending(row),
        hide: (row: QprDocumentRow) => this.submittingRows.has(row.qprDocSeq),
        onClick: (row: QprDocumentRow) => this.rejectDocument(row),
      },
    ];
  }

  ngOnInit(): void {
    this.buildColumns();

    this.translate.onLangChange.subscribe(() => {
      this.buildColumns();
    });

    this.loadQprDocuments();
  }

  /** `map` values aren't run through the translate pipe by the Table component, so they're
   * resolved here (and rebuilt on language change) rather than as raw i18n keys. */
  private buildColumns(): void {
    this.columns = [
      { key: 'clntNm', label: 'QPR_LISTING.COL_INSTITUTION' },
      { key: 'qrtr', label: 'QPR_LISTING.COL_QUARTER' },
      { key: 'docNm', label: 'QPR_LISTING.COL_DOCUMENT' },
      { key: 'crtdBy', label: 'QPR_LISTING.COL_CREATED_BY' },
      { key: 'crtdDt', label: 'QPR_LISTING.COL_CREATED_DATE', type: 'date' },
      {
        key: 'statusKey',
        label: 'QPR_LISTING.COL_STATUS',
        type: 'badge',
        map: {
          approved: this.translate.instant('QPR_LISTING.STATUS_APPROVED'),
          rejected: this.translate.instant('QPR_LISTING.STATUS_REJECTED'),
          pending: this.translate.instant('QPR_LISTING.STATUS_PENDING'),
        },
        badgeMap: STATUS_BADGE_MAP,
      },
    ];
  }

  private toStatusKey(aprvdFlg: number): QprStatusKey {
    if (aprvdFlg === 1) {
      return 'approved';
    }

    if (aprvdFlg === 2) {
      return 'rejected';
    }

    return 'pending';
  }

  get documentRows(): QprDocumentRow[] {
    return this.allDocuments.map((doc) => ({ ...doc, statusKey: this.toStatusKey(doc.aprvdFlg) }));
  }

  private loadQprDocuments(): void {
    this.loadingDocuments = true;
    this.hasDocumentsError = false;

    this.reportService.getQprDocuments().subscribe({
      next: (documents) => {
        this.allDocuments = Array.isArray(documents) ? documents : [];
        this.loadingDocuments = false;
      },
      error: (err) => {
        console.error('Failed to load QPR documents:', err);
        this.hasDocumentsError = true;
        this.loadingDocuments = false;
      },
    });
  }

  downloadReport(doc: QprDocument): void {
    if (!doc?.docImg) {
      this.toastr.warning(this.translate.instant('QPR_LISTING.NO_FILE'));
      return;
    }

    try {
      const cleanBase64 = this.cleanBase64(doc.docImg);

      if (!cleanBase64) {
        this.toastr.error(this.translate.instant('QPR_LISTING.INVALID_FILE'));
        return;
      }

      const byteCharacters = atob(cleanBase64);
      const byteArray = new Uint8Array(byteCharacters.length);

      for (let index = 0; index < byteCharacters.length; index++) {
        byteArray[index] = byteCharacters.charCodeAt(index);
      }

      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const baseFileName = (doc.docNm || 'QPR-Report').replace(/\.[^/.]+$/, '');

      link.href = objectUrl;
      link.download = `${baseFileName}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error('QPR document download failed:', error);
      this.toastr.error(this.translate.instant('QPR_LISTING.DOWNLOAD_FAILED'));
    }
  }

  approveDocument(doc: QprDocument): void {
    this.openConfirmDialog(doc, 1, 'QPR_LISTING.CONFIRM_APPROVE_MESSAGE');
  }

  rejectDocument(doc: QprDocument): void {
    this.openConfirmDialog(doc, 2, 'QPR_LISTING.CONFIRM_REJECT_MESSAGE');
  }

  private openConfirmDialog(doc: QprDocument, aprvdFlg: number, messageKey: string): void {
    this.pendingDoc = doc;
    this.pendingAprvdFlg = aprvdFlg;
    this.confirmMessage = this.translate.instant(messageKey);
    this.confirmDialogVisible = true;
  }

  /** Fires only on Confirm — Cancel (or the popup's own close) never emits this. */
  onConfirmAction(): void {
    if (!this.pendingDoc || this.pendingAprvdFlg === null) {
      return;
    }

    this.submitApproval(this.pendingDoc, this.pendingAprvdFlg);
    this.pendingDoc = null;
    this.pendingAprvdFlg = null;
  }

  /** Reuses the existing `/reports/qpr/submit` endpoint already used by the QPR Report page. */
  private submitApproval(doc: QprDocument, aprvdFlg: number): void {
    if (this.submittingRows.has(doc.qprDocSeq) || !this.isPending(doc)) {
      return;
    }

    this.submittingRows.add(doc.qprDocSeq);

    this.reportService
      .submitQprReport({ clntSeq: doc.clntSeq, qrtr: doc.qrtr, aprvdFlg })
      .subscribe({
        next: () => {
          this.submittingRows.delete(doc.qprDocSeq);

          // Update the row locally instead of re-fetching the whole list.
          // Approve/Reject icons hide automatically since `isPending()` reads
          // straight off `aprvdFlg` (1 = Approved, 2 = Rejected).
          this.allDocuments = this.allDocuments.map((item) =>
            item.qprDocSeq === doc.qprDocSeq ? { ...item, aprvdFlg } : item,
          );

          this.toastr.success(
            this.translate.instant(
              aprvdFlg === 1 ? 'QPR_LISTING.APPROVE_SUCCESS' : 'QPR_LISTING.REJECT_SUCCESS',
            ),
          );
        },
        error: (err) => {
          this.submittingRows.delete(doc.qprDocSeq);
          console.error('QPR document status update failed:', err);
          this.toastr.error(this.translate.instant('QPR_LISTING.STATUS_UPDATE_FAILED'));
        },
      });
  }

  private cleanBase64(value: string): string {
    if (!value) {
      return '';
    }

    let base64 = String(value).trim();
    const commaIndex = base64.indexOf(',');

    if (base64.startsWith('data:') && commaIndex !== -1) {
      base64 = base64.substring(commaIndex + 1);
    }

    return base64.replace(/^["']|["']$/g, '').replace(/\s/g, '');
  }
}
